import axios from 'axios';

const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const api = axios.create({
    baseURL: isDev ? 'http://localhost:5056/api' : '/api',
    timeout: 30000
});

api.interceptors.request.use(config => {
    const token = localStorage.getItem('sp_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            localStorage.removeItem('sp_token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authLogin = (data: any) => api.post('/auth/login', data);
export const authLoginOtp = (data: any) => api.post('/auth/login-otp', data);
export const authVerifyOtp = (data: any) => api.post('/auth/verify-otp', data);
export const authMe = () => api.get('/auth/me');

// Users
export const getUsers = (params?: any) => api.get('/users', { params });
export const getUserStats = () => api.get('/users/stats');
export const createUser = (data: any) => api.post('/users', data);
export const updateUser = (id: number, data: any) => api.put(`/users/${id}`, data);
export const toggleUserStatus = (id: number) => api.put(`/users/${id}/toggle-status`);
export const resetUserPassword = (id: number, data: any) => api.put(`/users/${id}/reset-password`, data);
export const getCommerciaux = () => api.get('/users/commerciaux');

// Commerciaux
export const getCommerciauxList = (params?: any) => api.get('/commerciaux', { params });
export const getCommercialDetail = (id: number) => api.get(`/commerciaux/${id}`);
export const getEquipes = () => api.get('/commerciaux/equipes');
export const createEquipe = (data: any) => api.post('/commerciaux/equipes', data);
export const updateEquipe = (id: number, data: any) => api.put(`/commerciaux/equipes/${id}`, data);
export const deleteEquipe = (id: number) => api.delete(`/commerciaux/equipes/${id}`);
export const setEquipeMembres = (id: number, data: any) => api.put(`/commerciaux/equipes/${id}/membres`, data);
export const getStatsEquipes = () => api.get('/commerciaux/stats-equipes');
export const changeCommercialEquipe = (id: number, data: any) => api.put(`/commerciaux/${id}/equipe`, data);
export const uploadUserPhoto = (id: number, file: File) => {
    const fd = new FormData(); fd.append('photo', file);
    return api.post(`/users/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteUserPhoto = (id: number) => api.delete(`/users/${id}/photo`);

// Clients
export const getClients = (params?: any) => api.get('/clients', { params });
export const getClient = (id: number) => api.get(`/clients/${id}`);
export const createClient = (data: any) => api.post('/clients', data);
export const updateClient = (id: number, data: any) => api.put(`/clients/${id}`, data);
export const addInteraction = (id: number, data: any) => api.post(`/clients/${id}/interactions`, data);
export const addFeedback = (id: number, data: any) => api.post(`/clients/${id}/feedbacks`, data);
export const getSegments = () => api.get('/clients/segments/list');
export const uploadClientPhoto = (id: number, file: File) => {
    const fd = new FormData(); fd.append('image', file);
    return api.post(`/clients/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadClientPhotoBoutique = (id: number, file: File) => {
    const fd = new FormData(); fd.append('image', file);
    return api.post(`/clients/${id}/photo-boutique`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Products
export const getProduits = (params?: any) => api.get('/produits', { params });
export const getProduit = (id: number) => api.get(`/produits/${id}`);
export const createProduit = (data: any) => api.post('/produits', data);
export const updateProduit = (id: number, data: any) => api.put(`/produits/${id}`, data);
export const getCategories = () => api.get('/produits/categories');
export const createCategorie = (data: any) => api.post('/produits/categories', data);
export const updateCategorie = (id: number, data: any) => api.put(`/produits/categories/${id}`, data);
export const deleteCategorie = (id: number) => api.delete(`/produits/categories/${id}`);

// Objectives
export const getObjectifs = (params?: any) => api.get('/objectifs', { params });
export const getObjectifsSuivi = (params?: any) => api.get('/objectifs/suivi', { params });
export const createObjectif = (data: any) => api.post('/objectifs', data);
export const updateObjectif = (id: number, data: any) => api.put(`/objectifs/${id}`, data);
export const deleteObjectif = (id: number) => api.delete(`/objectifs/${id}`);
export const ajusterAbsences = (data: any) => api.post('/objectifs/ajuster-absences', data);

// Activities
export const getActivites = (params?: any) => api.get('/activites', { params });
export const getActivite = (id: number) => api.get(`/activites/${id}`);
export const createActivite = (data: any) => api.post('/activites', data);
export const updateActivite = (id: number, data: any) => api.put(`/activites/${id}`, data);
export const syncActivites = (data: any) => api.post('/activites/sync', data);

// Dashboard
export const getDashboard = () => api.get('/dashboard');
export const getDashboardTendances = (annee: number) => api.get(`/dashboard/tendances/${annee}`);
export const getDashboardObjectifs = (params?: any) => api.get('/dashboard/objectifs-resume', { params });

// Performance
export const getPerformanceKpi = (params?: any) => api.get('/performance/kpi', { params });
export const getClassement = (params?: any) => api.get('/performance/classement', { params });
export const getEvolution = (params?: any) => api.get('/performance/evolution', { params });
export const getComparaison = (params?: any) => api.get('/performance/comparaison', { params });

// Gamification
export const getLeaderboard = (params?: any) => api.get('/gamification/leaderboard', { params });
export const getPoints = (userId: string) => api.get(`/gamification/points/${userId}`);
export const getBadges = () => api.get('/gamification/badges');
export const getChallenges = (params?: any) => api.get('/gamification/challenges', { params });
export const createChallenge = (data: any) => api.post('/gamification/challenges', data);
export const joinChallenge = (id: number) => api.post(`/gamification/challenges/${id}/join`);

// Bonus
export const getBonusRegles = () => api.get('/bonus/regles');
export const createBonusRegle = (data: any) => api.post('/bonus/regles', data);
export const updateBonusRegle = (id: number, data: any) => api.put(`/bonus/regles/${id}`, data);
export const deleteBonusRegle = (id: number) => api.delete(`/bonus/regles/${id}`);
export const calculerBonus = (data: any) => api.post('/bonus/calculer', data);
export const getBonusCalculs = (params?: any) => api.get('/bonus/calculs', { params });
export const validerBonus = (id: number, data?: any) => api.put(`/bonus/calculs/${id}/valider`, data);
export const validerBonusMasse = (data: any) => api.put('/bonus/calculs/valider-masse', data);
export const getSimulationBonus = (params?: any) => api.get('/bonus/simulation', { params });
export const getBonusStats = () => api.get('/bonus/stats');

// Geolocation
export const trackPosition = (data: any) => api.post('/geolocation/track', data);
export const trackPositionBatch = (data: any) => api.post('/geolocation/track-batch', data);
export const getPositions = () => api.get('/geolocation/positions');
export const getHistoriqueGps = (userId: number, params?: any) => api.get(`/geolocation/historique/${userId}`, { params });
export const getHeatmap = (params?: any) => api.get('/geolocation/heatmap', { params });
export const getCouverture = () => api.get('/geolocation/couverture');

// Geofencing
export const getGeofences = () => api.get('/geofencing');
export const createGeofence = (data: any) => api.post('/geofencing', data);
export const updateGeofence = (id: number, data: any) => api.put(`/geofencing/${id}`, data);
export const checkGeofence = (data: any) => api.post('/geofencing/check', data);
export const getGeofenceAlertes = (params?: any) => api.get('/geofencing/alertes', { params });
export const detectAnomalies = () => api.post('/geofencing/anomalies');

// Planning
export const getPlanifications = (params?: any) => api.get('/planning', { params });
export const createPlanification = (data: any) => api.post('/planning', data);
export const updatePlanification = (id: number, data: any) => api.put(`/planning/${id}`, data);
export const deletePlanification = (id: number) => api.delete(`/planning/${id}`);
export const getSuggestions = (params?: any) => api.get('/planning/suggestions', { params });

// Reporting
export const getReportCommercial = (id: number, params?: any) => api.get(`/reporting/commercial/${id}`, { params });
export const getReportZone = (id: number, params?: any) => api.get(`/reporting/zone/${id}`, { params });
export const getReportProduit = (id: number, params?: any) => api.get(`/reporting/produit/${id}`, { params });
export const getReportGlobal = (params?: any) => api.get('/reporting/global', { params });

// Absences
export const getAbsences = (params?: any) => api.get('/absences', { params });
export const createAbsence = (data: any) => api.post('/absences', data);
export const approuverAbsence = (id: number) => api.put(`/absences/${id}/approuver`);
export const refuserAbsence = (id: number) => api.put(`/absences/${id}/refuser`);
export const deleteAbsence = (id: number) => api.delete(`/absences/${id}`);

// Sync
export const getSyncStatus = () => api.get('/sync/status');
export const getSyncQueue = () => api.get('/sync/queue');
export const forceSync = () => api.post('/sync/force');
export const resolveConflict = (data: any) => api.post('/sync/resolve-conflict', data);

// Reporting exports
export const exportReportPdf = (params?: any) => api.get('/reporting/export/pdf', { params, responseType: 'blob' });
export const exportReportCsv = (params?: any) => api.get('/reporting/export/csv', { params, responseType: 'blob' });
export const exportBonusCsv = (params?: any) => api.get('/bonus/export', { params, responseType: 'blob' });
export const exportBonusPdf = (params?: any) => api.get('/bonus/export?format=pdf', { params, responseType: 'blob' });

// Analytics
export const getAnalyticsKpi = () => api.get('/analytics/kpi');
export const getPredictions = () => api.get('/analytics/predictions');
export const getRisques = () => api.get('/analytics/risques');
export const getRecommandations = () => api.get('/analytics/recommandations');
export const getTendances = () => api.get('/analytics/tendances');
export const getAnalyticsClassement = (params?: any) => api.get('/analytics/classement', { params });
export const getAnalyticsOverview = () => api.get('/analytics/overview');
export const getPredictionsEnriched = () => api.get('/analytics/predictions-enriched');

// Tracking / Audit
export const getTrackingStats = () => api.get('/tracking/stats');
export const getTrackingLogs = (params?: any) => api.get('/tracking/logs', { params });

// Notifications
export const getNotifications = (params?: any) => api.get('/notifications', { params });
export const markNotificationRead = (id: number) => api.put(`/notifications/${id}/lire`);
export const markAllNotificationsRead = () => api.put('/notifications/lire-tout');

// Zones
export const getZones = () => api.get('/zones');
export const createZone = (data: any) => api.post('/zones', data);
export const updateZone = (id: number, data: any) => api.put(`/zones/${id}`, data);
export const getZoneArborescence = () => api.get('/zones/arborescence');
export const getZoneDetail = (id: number) => api.get(`/zones/${id}`);
export const getZoneStats = (id: number) => api.get(`/zones/${id}/stats`);
export const deleteZone = (id: number) => api.delete(`/zones/${id}`);
export const assignZoneResponsable = (id: number, data: any) => api.put(`/zones/${id}/responsable`, data);

// Admin - Roles
export const getAdminRoles = () => api.get('/admin/roles');
export const getAdminRole = (id: number) => api.get(`/admin/roles/${id}`);
export const createAdminRole = (data: any) => api.post('/admin/roles', data);
export const updateAdminRole = (id: number, data: any) => api.put(`/admin/roles/${id}`, data);
export const deleteAdminRole = (id: number) => api.delete(`/admin/roles/${id}`);
export const getRolePermissions = (id: number) => api.get(`/admin/roles/${id}/permissions`);
export const setRolePermissions = (id: number, data: any) => api.put(`/admin/roles/${id}/permissions`, data);
export const getRoleScopes = (id: number) => api.get(`/admin/roles/${id}/scopes`);
export const setRoleScopes = (id: number, data: any) => api.put(`/admin/roles/${id}/scopes`, data);

// Admin - Permissions
export const getAdminPermissions = () => api.get('/admin/permissions');
export const getPermissionMatrix = () => api.get('/admin/permissions/matrix');
export const getAdminModules = () => api.get('/admin/permissions/modules');
export const toggleModule = (code: string, data: any) => api.put(`/admin/permissions/modules/${code}`, data);

// Admin - Security
export const getPasswordPolicy = () => api.get('/admin/security/password-policy');
export const updatePasswordPolicy = (data: any) => api.put('/admin/security/password-policy', data);
export const getActiveSessions = (params?: any) => api.get('/admin/security/sessions', { params });
export const revokeAllSessions = () => api.post('/admin/security/revoke-all-sessions');
export const getLockedAccounts = () => api.get('/admin/security/locked-accounts');
export const unlockUser = (id: number) => api.put(`/admin/security/unlock/${id}`);

// Admin - Audit
export const getAuditLogs = (params?: any) => api.get('/admin/audit/logs', { params });
export const getAuditStats = () => api.get('/admin/audit/stats');
export const purgeAuditLogs = () => api.post('/admin/audit/purge');

// Admin - Settings
export const getAppSettings = () => api.get('/admin/settings');
export const updateAppSetting = (key: string, data: any) => api.put(`/admin/settings/${key}`, data);
export const updateAppSettingsBulk = (data: any) => api.put('/admin/settings/bulk', data);

// Auth - New
export const changePassword = (data: any) => api.post('/auth/change-password', data);
export const authLogout = () => api.post('/auth/logout');

// Suivi Mensuel (objectifs + ventes par client/mois)
export const getCalendrierVentes = (annee: number) => api.get('/suivi-mensuel/calendrier', { params: { annee } });
export const updateCalendrierVentes = (annee: number, mois: number, data: any) => api.put(`/suivi-mensuel/calendrier/${annee}/${mois}`, data);
export const getRepartitionObjectifs = (annee: number) => api.get('/suivi-mensuel/repartition', { params: { annee } });
export const updateRepartitionObjectifs = (annee: number, data: any) => api.put(`/suivi-mensuel/repartition/${annee}`, data);
export const getObjectifsClients = (params: any) => api.get('/suivi-mensuel/objectifs', { params });
export const updateObjectifClient = (client_id: number, annee: number, data: any) => api.put(`/suivi-mensuel/objectifs/${client_id}/${annee}`, data);
export const applyRepartitionObjectifs = (data: any) => api.post('/suivi-mensuel/objectifs/apply-repartition', data);
export const getVentesMensuelles = (params: any) => api.get('/suivi-mensuel/ventes', { params });
export const updateVenteMensuelle = (client_id: number, annee: number, mois: number, data: any) => api.put(`/suivi-mensuel/ventes/${client_id}/${annee}/${mois}`, data);
export const bulkUpdateVentesMensuelles = (data: any) => api.post('/suivi-mensuel/ventes/bulk', data);
export const syncVentesFromActivites = (data: any) => api.post('/suivi-mensuel/sync-activites', data);
export const getSuiviParCommercial = (params: any) => api.get('/suivi-mensuel/par-commercial', { params });
export const getSuiviParZone = (params: any) => api.get('/suivi-mensuel/par-zone', { params });
export const getSuiviParCategorie = (params: any) => api.get('/suivi-mensuel/par-categorie', { params });

// Portefeuilles Clients
export const getPortefeuilles = (params?: any) => api.get('/portefeuilles', { params });
export const getPortefeuilleNonAffectes = () => api.get('/portefeuilles/non-affectes');
export const getPortefeuilleCommercial = (id: number) => api.get(`/portefeuilles/commercial/${id}`);
export const getPortefeuilleClient = (id: number) => api.get(`/portefeuilles/client/${id}`);
export const updatePortefeuilleClient = (id: number, data: any) => api.put(`/portefeuilles/client/${id}`, data);
export const transferPortefeuille = (data: any) => api.post('/portefeuilles/transfer', data);
export const bulkAssignPortefeuille = (data: any) => api.post('/portefeuilles/bulk-assign', data);
export const getPortefeuilleRegles = () => api.get('/portefeuilles/regles');
export const createPortefeuilleRegle = (data: any) => api.post('/portefeuilles/regles', data);
export const updatePortefeuilleRegle = (id: number, data: any) => api.put(`/portefeuilles/regles/${id}`, data);
export const deletePortefeuilleRegle = (id: number) => api.delete(`/portefeuilles/regles/${id}`);
export const applyPortefeuilleRegle = (id: number) => api.post(`/portefeuilles/regles/${id}/apply`);
export const getPortefeuilleDashboard = () => api.get('/portefeuilles/dashboard');
export const getMonPortefeuille = () => api.get('/portefeuilles/me');

export default api;
